// Das Muster "Pflegeabschnitt": Rahmen, Statusregion, Liste, Historie und die
// Beendigung einer Zeile -- einmal gebaut fuer Kennungen, Kontaktwege und
// Adressen.
//
// Der Bildschirm liefert nur noch das Fachliche: die Felder seines Formulars,
// den Inhalt einer Zeile, seine Saetze und seine Befehle. Was jeder
// Pflegebildschirm gleich tun muss, steht hier:
//   * GENAU EINE Region mit aria-live. Eine zweite machte "die Statusmeldung"
//     mehrdeutig -- fuer Hilfsmittel wie fuer die Proben. Sie ist das
//     Fokusziel nach jedem Befehl, ausser nach einer Feldablehnung: dann steht
//     der Fokus im ersten markierten Feld des Rahmens (use-section-commands.ts).
//   * "Erneut versuchen" nach einem abgebrochenen Anlegen steht UNMITTELBAR
//     nach der Statusregion und ausserhalb von ihr: der naechste
//     Tabulatorschritt erreicht ihn, und die Region sagt nur Text an.
//   * Der Rueckweg steht zuerst; die Fokusfuehrung haengt an der Region, nicht
//     an der Position.
//   * Nach einem Konflikt steht "neu laden" da und ausdruecklich KEIN "erneut
//     versuchen": ein zweiter Versuch mit demselben Stand endete genauso.
//   * Die Beendigung erscheint erst auf Anforderung. Beim Oeffnen geht der
//     Fokus auf die Frage -- oder, wo ein Grund zu waehlen ist, auf den
//     gewaehlten Grund --, nie auf den Knopf, der beendet: ein zweites Enter
//     beendete sonst, ohne dass die Wirkung gelesen wurde. Nur "Abbrechen"
//     gibt den Fokus an "Beenden" zurueck (WCAG 2.4.3).
//   * Die Historie liegt zugeklappt: sie belegt "beendet statt geloescht",
//     ist aber nicht die Arbeit.
//
// EIGENSTAENDIG traegt die Seite Brotkrume und h1 selbst (screens/pflege-kopf.tsx,
// v4); der Rueckweg ist der Name in der Brotkrume, ein zweiter Link entfaellt.
//
// EINGEBETTET (`eingebettet`), als Abschnitt im Kontaktdetail statt als
// eigener Bildschirm: ohne Card, denn man ist schon am Kontakt. Die Statusregion steht dann NACH dem Inhalt und traegt den Namen
// "Status: <Abschnitt>" -- wie alle Abschnitte dort, denn die Handlung (das
// Formular) steht unten. Laden steht ausserhalb der Region: das Kontaktdetail
// laedt mehrere Abschnitte zugleich, und jeder sagte sonst sein Laden an. Kein
// Knopf ist primaer; die Hauptaktion der Seite bleibt "Notiz festhalten"
// (NFR-UX-004).
//
// BEENDEN OHNE RUECKFRAGE (`Zeilenaktionen` mit `sofort`, ADR-0061 Ziffer 17):
// wo das Ende nichts loescht und der Rueckweg im selben Abschnitt liegt,
// beendet der Knopf sofort. Die Bestaetigung in der Statusregion nennt dann die
// Wirkung UND den Rueckweg; der Fokus geht dorthin, auch wenn die Zeile mit
// dem Knopf verschwindet (WCAG 2.4.3).

import { useCallback, useEffect, useId, useRef, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Body1,
  Button,
  Caption1,
  Card,
  Field,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Radio,
  RadioGroup,
  Spinner,
  Subtitle1,
  tokens,
} from '@fluentui/react-components';

import { useScreenStyles } from './screen-styles.ts';
import type { MaintainedSection } from './use-maintained-section.ts';
import { meldungsIntent } from './use-section-commands.ts';
import { VorgangNach } from './zustand.tsx';
import { NebenKnopf } from './nebenknopf.tsx';

export const usePflegeStyles = makeStyles({
  gruppe: { marginTop: '1.5rem' },
  // h2 links, Hauptaktion rechts; schmal bricht sie unter die Ueberschrift.
  // Die h2 ohne Browser-Rand: sonst steht sie 16,6 px unter dem Kartenrand und schiebt den Knopf daneben mit.
  listenkopf: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem 1rem', '& > h2': { margin: 0 } },
  liste: {
    listStyle: 'none',
    margin: '0.5rem 0 0',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  eintrag: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingBottom: '0.75rem',
  },
  wert: { display: 'block', overflowWrap: 'anywhere' },
  merkmale: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.25rem',
  },
  // inline-flex: die Aktionsgruppen einer Zeile (Beenden, Auch verwenden als, Anschrift
  // korrigieren) stehen als EINE Reihe kleiner Knoepfe (v4) und brechen schmal um.
  zeilenaktionen: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginTop: '0.5rem',
    marginInlineEnd: '0.5rem',
  },
  // Kleiner Umrissknopf wie v4 .btn.small: 28 px statt Fluent small (24, genau die Untergrenze aus WCAG 2.5.8).
  zeilenknopf: { minHeight: '28px' },
  // Bei nur einem Zweck ist dessen Rahmen ohne eigene Zeile: Beenden steht in derselben
  // Reihe wie "Auch verwenden als" und "Anschrift korrigieren" (der Rahmen traegt keine Semantik).
  zweckEinzeln: { display: 'contents' },
  // Die Linie links heisst ueberall "gehoert zur Zeile darueber" und bleibt. Schmal
  // (in rem, also auch bei grosser Browser-Grundschrift) kostet der Einzug die
  // Feldbreite und schrumpft deshalb auf 8 px (DESIGN-P3 zu #632).
  beenden: {
    marginTop: '0.75rem',
    paddingLeft: '0.75rem',
    borderLeft: `2px solid ${tokens.colorBrandStroke1}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    '@media (max-width: 22.5rem)': { paddingLeft: tokens.spacingHorizontalS },
  },
  hinweis: { display: 'block', marginTop: '0.5rem', color: tokens.colorNeutralForeground2 },
  // Der Leersatz steht an der Stelle des Aufklappers: der Abstand der Gruppe gilt, kein eigener (DESIGN zu B5).
  leersatz: { display: 'block', color: tokens.colorNeutralForeground2 },
  status: { marginTop: '0.5rem' },
  // minWidth 0: Fluents Input haelt sonst seine Mindestbreite und ragt bei 320 px
  // und 200 % Text aus der Feldspalte ueber den Fensterrand.
  feld: { width: '100%', minWidth: 0 },
});

export interface PflegeabschnittTexte {
  /** Die Ansage des Spinners, etwa "Kennungen werden geladen". */
  readonly laden: string;
  /** Titel und Satz der Feldablehnung, etwa "Die Kennung konnte nicht übernommen werden." */
  readonly feldfehlerTitel: string;
  readonly feldfehlerText: string;
  /**
   * Der Knopf nach einem Konflikt, etwa "Kennungen neu laden". Fehlt er,
   * steht keiner da: der Abschnitt laedt nach einem Konflikt ohnehin still nach.
   */
  readonly neuLaden?: string;
  /** Der Knopf nach einem Ladefehler; sonst "Erneut versuchen". */
  readonly erneutLaden?: string;
  /**
   * Die Erklaerung zum unbekannten Kontakt. Wo "Adresse" auf dem Bildschirm
   * schon die Anschrift meint, sagt sie "Link" statt "Adresse".
   */
  readonly unbekanntErklaerung?: string;
}

/**
 * Der Rahmen eines Pflegebildschirms. `children` erscheint erst, wenn der
 * Abschnitt geladen ist; `zusatzmeldung` steht in der Statusregion vor der
 * Bestaetigung (etwa das Ergebnis des Einfuegens einer Anschrift).
 */
export function Pflegeabschnitt<Data>({
  abschnitt,
  texte,
  eingebettet,
  onErneutAnlegen,
  zusatzmeldung,
  children,
}: {
  readonly abschnitt: MaintainedSection<Data>;
  /** Der Kontakt des Abschnitts; der Rahmen selbst braucht ihn seit der Brotkrume nicht mehr. */
  readonly partyId: string;
  readonly texte: PflegeabschnittTexte;
  /** Als Abschnitt im Kontaktdetail; `name` benennt die Statusregion ("Status: <name>"). */
  readonly eingebettet?: { readonly name: string; readonly statusId?: string };
  /** "Erneut versuchen" nach einem abgebrochenen Anlegen -- dieselbe Eingabe, dieselbe Absendekennung. */
  readonly onErneutAnlegen: () => void;
  readonly zusatzmeldung?: ReactNode;
  readonly children: (data: Data) => ReactNode;
}): ReactElement {
  const styles = useScreenStyles();
  const own = usePflegeStyles();
  const { status, data, message, messageOffen, confirmation, confirmationTitle, fieldErrors, busy, submitFailed, retryCommand, retryLabel, statusRef, sectionRef, reload } =
    abschnitt;
  // Ein Rueckruf statt des Ref-Objekts: Fluent typisiert den `ref` der Card
  // unter React 19 als Ref<never>, und nur ein Rueckruf passt darauf.
  const rahmen = useCallback(
    (knoten: HTMLDivElement | null) => {
      sectionRef.current = knoten;
    },
    [sectionRef],
  );
  const spinner = status === 'laedt' ? <Spinner labelPosition="after" label={texte.laden} /> : undefined;
  const region = (
    <>
    <div
      aria-live="polite"
      role="status"
      ref={statusRef}
      tabIndex={-1}
      {...(eingebettet === undefined
        ? {}
        : { 'aria-label': `Status: ${eingebettet.name}`, className: own.status, ...(eingebettet.statusId === undefined ? {} : { id: eingebettet.statusId }) })}
    >
      {eingebettet === undefined ? spinner : undefined}
      {status === 'unbekannt' ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Diesen Kontakt gibt es hier nicht.</MessageBarTitle>
            {/* KEINE VERMUTUNG ueber eine Berechtigung -- der Server
                antwortet auf "gibt es nicht" und "darfst du nicht sehen"
                mit demselben Ausgang. */}
            <div>{texte.unbekanntErklaerung ?? 'Er wurde entfernt, oder die Adresse ist nicht mehr gültig.'}</div>
            <div>Geh zurück zur Liste und suche ihn dort.</div>
          </MessageBarBody>
        </MessageBar>
      ) : undefined}
      {zusatzmeldung}
      {confirmation !== undefined ? (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>{confirmationTitle ?? 'Gespeichert'}</MessageBarTitle>
            <div>{confirmation}</div>
          </MessageBarBody>
        </MessageBar>
      ) : undefined}
      {fieldErrors.length > 0 ? (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>{texte.feldfehlerTitel}</MessageBarTitle>
            <div>{texte.feldfehlerText}</div>
          </MessageBarBody>
        </MessageBar>
      ) : undefined}
      {message !== undefined ? (
        <MessageBar intent={meldungsIntent(message, messageOffen)}>
          <MessageBarBody>
            <MessageBarTitle>{message.headline}</MessageBarTitle>
            <div>{message.explanation}</div>
            <div>{message.nextStep}</div>
          </MessageBarBody>
        </MessageBar>
      ) : undefined}
    </div>
    {/* Hinter der Region, nicht in ihr (keine verschachtelte Live-Region). */}
    <VorgangNach meldungen={[message]} />
    </>
  );
  // Eingebettet ist kein Knopf primaer (NFR-UX-004).
  const betont = eingebettet === undefined ? 'primary' : 'secondary';
  const erneut = retryCommand ?? (submitFailed ? onErneutAnlegen : undefined);
  const wiederholen =
    erneut !== undefined && status === 'geladen' ? (
      <div className={styles.actions}>
        <Button
          appearance={betont}
          disabled={busy}
          onClick={erneut}
          {...(retryCommand !== undefined && retryLabel !== undefined ? { 'aria-label': retryLabel } : {})}
        >
          Erneut versuchen
        </Button>
      </div>
    ) : undefined;
  const inhalt = status === 'geladen' && data !== undefined ? children(data) : undefined;
  const neuLaden = (
    <div className={styles.actions}>
      {status === 'abgelehnt' && message?.retryable === true ? (
        <Button appearance={betont} onClick={reload}>
          {texte.erneutLaden ?? 'Erneut versuchen'}
        </Button>
      ) : undefined}
      {texte.neuLaden !== undefined && message !== undefined && message.retryable === false && status === 'geladen' ? (
        <Button appearance="secondary" onClick={reload}>
          {texte.neuLaden}
        </Button>
      ) : undefined}
    </div>
  );

  if (eingebettet !== undefined) {
    return (
      <div ref={rahmen}>
        {spinner}
        {inhalt}
        {region}
        {wiederholen}
        {neuLaden}
      </div>
    );
  }
  return (
    <Card className={styles.card} ref={rahmen}>
      {region}
      {wiederholen}
      {inhalt}
      {neuLaden}
    </Card>
  );
}

/** Die gueltigen Eintraege unter ihrer Ueberschrift, mit Leerzustand und Ueberlaufhinweis. */
export function Pflegeliste({
  id,
  titel,
  leer,
  anzahl,
  hinweis,
  aktion,
  vorListe,
  children,
}: {
  readonly id: string;
  readonly titel: string;
  readonly leer: string;
  readonly anzahl: number;
  readonly hinweis?: string | undefined;
  /** Die Hauptaktion am Kopf der Liste (v4 "+ Kontaktweg"), etwa der Oeffner des Formulars. */
  readonly aktion?: ReactNode;
  /** Steht zwischen Kopf und Eintraegen, etwa das geoeffnete Formular. */
  readonly vorListe?: ReactNode;
  readonly children: ReactNode;
}): ReactElement {
  const own = usePflegeStyles();
  return (
    <section className={own.gruppe} aria-labelledby={id}>
      <div className={own.listenkopf}>
        <Subtitle1 as="h2" id={id}>
          {titel}
        </Subtitle1>
        {aktion}
      </div>
      {/* Leer steht der Satz direkt unter der h2 und erst dann ein offenes Formular -- sonst
          laese man das Formular als die Liste (DESIGN 12.10.2026). */}
      {anzahl === 0 ? <Body1 className={own.hinweis}>{leer}</Body1> : undefined}
      {vorListe}
      {anzahl === 0 ? undefined : <ul className={own.liste}>{children}</ul>}
      {hinweis === undefined ? undefined : <Caption1 className={own.hinweis}>{hinweis}</Caption1>}
    </section>
  );
}

/** Die beendeten Eintraege, zugeklappt. Die Zahl steht in der Ueberschrift. */
export function Pflegehistorie({
  titel,
  anzahl,
  leer,
  abgeschnitten,
  children,
}: {
  readonly titel: string;
  readonly anzahl: number;
  readonly leer: string;
  /** Der Satz unter einer abgeschnittenen Historie, sonst nichts. */
  readonly abgeschnitten?: string | undefined;
  readonly children: ReactNode;
}): ReactElement {
  const own = usePflegeStyles();
  // Ohne Eintraege kein Aufklapper, der nichts zeigt (Restliste B5): nur der Satz.
  if (anzahl === 0 && abgeschnitten === undefined) {
    return (
      <div className={own.gruppe}>
        <Body1 className={own.leersatz}>{leer}</Body1>
      </div>
    );
  }
  return (
    <div className={own.gruppe}>
      <Accordion collapsible>
        <AccordionItem value="historie">
          <AccordionHeader>
            {titel} ({anzahl})
          </AccordionHeader>
          <AccordionPanel>
            {anzahl === 0 ? <Body1 className={own.hinweis}>{leer}</Body1> : <ul className={own.liste}>{children}</ul>}
            {abgeschnitten === undefined ? undefined : <Caption1 className={own.hinweis}>{abgeschnitten}</Caption1>}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

export interface Endgruende {
  readonly optionen: readonly { readonly code: string; readonly label: string; readonly hint: string }[];
  readonly wert: string;
  readonly onWert: (code: string) => void;
  readonly id?: string;
}

interface ZeilenaktionenBasis {
  readonly name: string;
  readonly busy: boolean;
  readonly onBeenden: () => void;
  readonly children?: ReactNode;
  /**
   * Das Verb des Knopfes. "Beenden" passt, wo etwas ein Ende in der Zeit hat
   * (Beziehung, Rolle); Kontaktwege, Anschriften und Kennungen werden "nicht
   * mehr verwendet" (Glossar).
   */
  readonly aktion?: string;
}

/** Beenden mit Rueckfrage: die Frage erscheint erst auf Anforderung. */
interface MitRueckfrage extends ZeilenaktionenBasis {
  readonly sofort?: false;
  readonly offen: boolean;
  /** Ohne Gruende die Frage, mit Gruenden die Beschriftung der Auswahl. */
  readonly frage: string;
  /** Ohne Gruende: die Kennung der Frage, die die Gruppe beschriftet. Fehlt sie, entsteht eine eigene -- eine Gruppe ohne Namen gibt es nicht. */
  readonly frageId?: string;
  /** Die Wirkung in einem Satz VOR der Entscheidung. */
  readonly wirkung: string;
  readonly bestaetigen: string;
  readonly gruende?: Endgruende;
  readonly onOeffnen: () => void;
  readonly onAbbrechen: () => void;
}

/**
 * Beenden ohne Rueckfrage (ADR-0061 Ziffer 17): nur, wo nichts geloescht wird
 * und der Rueckweg im selben Abschnitt liegt. Wirkung und Rueckweg sagt die
 * Bestaetigung des Befehls in der Statusregion.
 */
interface OhneRueckfrage extends ZeilenaktionenBasis {
  readonly sofort: true;
}

/**
 * Die Aktionen einer Zeile samt ihrer Beendigung.
 *
 * `children` sind die weiteren Zeilenaktionen -- BEWUSST zurueckhaltend als
 * kleine Umrissknoepfe (`secondary`, `size="small"`, Klasse `zeilenknopf`),
 * damit die Hauptaktion des Bildschirms sichtbar bleibt (NFR-UX-004). `name` steht im Namen des Knopfes "Beenden": in einer
 * Liste hiessen die Knoepfe sonst alle gleich.
 */
export function Zeilenaktionen(props: MitRueckfrage | OhneRueckfrage): ReactElement {
  return props.sofort === true ? <SofortBeenden {...props} /> : <BeendenMitRueckfrage {...props} />;
}

function SofortBeenden({ name, busy, onBeenden, children, aktion = 'Beenden' }: OhneRueckfrage): ReactElement {
  const own = usePflegeStyles();
  return (
    <div className={own.zeilenaktionen}>
      {children}
      <Button appearance="secondary" size="small" className={own.zeilenknopf} disabled={busy} data-aktion="beenden" aria-label={`${aktion}: ${name}`} onClick={onBeenden}>
        {aktion}
      </Button>
    </div>
  );
}

function BeendenMitRueckfrage({
  name,
  offen,
  busy,
  frage,
  frageId,
  wirkung,
  bestaetigen,
  gruende,
  onOeffnen,
  onAbbrechen,
  onBeenden,
  children,
  aktion = 'Beenden',
}: MitRueckfrage): ReactElement {
  const own = usePflegeStyles();
  const eigeneFrageId = useId();
  const frageKennung = frageId ?? eigeneFrageId;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const aktionenRef = useRef<HTMLDivElement | null>(null);
  const warOffen = useRef(false);
  const abgebrochen = useRef(false);
  useEffect(() => {
    if (offen) {
      const panel = panelRef.current;
      if (gruende === undefined) {
        panel?.focus();
      } else {
        (
          panel?.querySelector<HTMLInputElement>('input[type="radio"]:checked') ??
          panel?.querySelector<HTMLInputElement>('input[type="radio"]')
        )?.focus();
      }
    } else if (warOffen.current && abgebrochen.current) {
      aktionenRef.current?.querySelector<HTMLButtonElement>('button[data-aktion="beenden"]')?.focus();
    }
    warOffen.current = offen;
    abgebrochen.current = false;
    // Nur das Oeffnen und Schliessen fuehrt den Fokus, nicht jeder Rerender.
  }, [offen]);

  const abbrechen = (): void => {
    abgebrochen.current = true;
    onAbbrechen();
  };
  // Esc wie "Abbrechen", der Fokus geht an den Ausloeser zurueck -- ausser eine
  // offene Auswahlliste (aria-expanded) gehoert dazu: dann schliesst Esc nur sie.
  const beiEscape = (event: KeyboardEvent<HTMLDivElement>): void => {
    // Waehrend der Server antwortet, bleibt die Rueckfrage stehen -- wie "Abbrechen".
    if (busy || event.key !== 'Escape' || event.defaultPrevented) return;
    if (event.target instanceof HTMLElement && event.target.getAttribute('aria-expanded') === 'true') return;
    event.stopPropagation();
    abbrechen();
  };

  const knoepfe = (
    <div className={own.zeilenaktionen}>
      <Button appearance="primary" disabled={busy || gruende?.wert === ''} onClick={onBeenden}>
        {bestaetigen}
      </Button>
      <NebenKnopf
        disabled={busy}
        onClick={abbrechen}
      >
        Abbrechen
      </NebenKnopf>
    </div>
  );

  return (
    <>
      <div className={own.zeilenaktionen} ref={aktionenRef}>
        {children}
        {offen ? undefined : (
          <Button
            appearance="secondary"
            size="small"
            className={own.zeilenknopf}
            disabled={busy}
            data-aktion="beenden"
            aria-label={`${aktion}: ${name}`}
            onClick={onOeffnen}
          >
            {aktion}
          </Button>
        )}
      </div>
      {offen && gruende === undefined ? (
        <div className={own.beenden} ref={panelRef} tabIndex={-1} role="group" aria-labelledby={frageKennung} onKeyDown={beiEscape}>
          <Body1 id={frageKennung}>{frage}</Body1>
          <Caption1>{wirkung}</Caption1>
          {knoepfe}
        </div>
      ) : undefined}
      {/* Mit Gruenden beschriftet die Frage die Auswahl, nicht die Gruppe; die Gruppe
          heisst nach der Zeile ("Rückfrage: <Name>"), damit ein Screenreader beim
          Eintritt hoert, wessen Beendigung gefragt ist -- wie im Stichwortkatalog. */}
      {offen && gruende !== undefined ? (
        <div className={own.beenden} ref={panelRef} role="group" aria-label={`Rückfrage: ${name}`} onKeyDown={beiEscape}>
          <Field label={frage} required>
            <RadioGroup
              {...(gruende.id === undefined ? {} : { id: gruende.id })}
              value={gruende.wert}
              onChange={(_event, data) => gruende.onWert(data.value)}
            >
              {gruende.optionen.map((eintrag) => (
                <Radio key={eintrag.code} value={eintrag.code} label={eintrag.label} />
              ))}
            </RadioGroup>
          </Field>
          {/* Die WIRKUNG des gewaehlten Grundes gehoert vor die Entscheidung. */}
          <Caption1>{gruende.optionen.find((eintrag) => eintrag.code === gruende.wert)?.hint}</Caption1>
          <Caption1>{wirkung}</Caption1>
          {knoepfe}
        </div>
      ) : undefined}
    </>
  );
}
